import { findCountryFeature, isCountryLocation } from "@/utils/data/geography";
import { hexToRgb } from "@/utils/format";
import type { Layer } from "@deck.gl/core";
import { GeoJsonLayer, ScatterplotLayer } from "@deck.gl/layers";
import type { MapLayerState, MapLayerActions } from "@/types/stores/map";

export function createTemporaryLayerActions(
  set: any,
  get: () => MapLayerState & MapLayerActions
) {
  return {
    addTemporaryAiMarker: async (
      workspaceId: string,
      coordinates: [number, number],
      locationName?: string,
      zoom?: number
    ) => {
      const state = get();
      const instanceState = state.mapInstances[workspaceId];

      if (!instanceState?.isLoaded) {
        return;
      }

      get().removeTemporaryAiMarker(workspaceId);

      try {
        const isCountry = locationName && isCountryLocation(zoom);
        let countryFeature: any = null;

        if (isCountry) {
          countryFeature = await findCountryFeature(
            locationName,
            state.staticData
          );
        }

        const temporaryLayers: Layer[] = [];

        if (countryFeature) {
          const countryData = {
            type: "FeatureCollection" as const,
            features: [countryFeature],
          };

          temporaryLayers.push(
            new GeoJsonLayer({
              id: "ai-temporary-marker-fill",
              data: countryData,
              pickable: false,
              stroked: false,
              filled: true,
              getFillColor: hexToRgb("#00297A", 26),
            }),
            new GeoJsonLayer({
              id: "ai-temporary-marker-border",
              data: countryData,
              pickable: false,
              stroked: true,
              filled: false,
              getLineColor: hexToRgb("#00297A", 204),
              getLineWidth: 2,
              lineWidthMinPixels: 1,
            })
          );
        } else {
          temporaryLayers.push(
            new ScatterplotLayer({
              id: "ai-temporary-marker",
              data: [
                {
                  type: "Feature",
                  properties: {},
                  geometry: {
                    type: "Point",
                    coordinates: coordinates,
                  },
                },
              ],
              pickable: false,
              radiusScale: 1,
              radiusMinPixels: 7,
              radiusMaxPixels: 7,
              getPosition: (d: any) => {
                const coords = d.geometry?.coordinates;
                return coords ? [coords[0], coords[1]] : [0, 0];
              },
              getFillColor: hexToRgb("#032871", 255),
              getLineColor: [255, 255, 255, 255],
              getRadius: 7.5,
              lineWidthMinPixels: 1,
            })
          );
        }

        set(
          (state: MapLayerState) => ({
            temporaryLayers: {
              ...state.temporaryLayers,
              [workspaceId]: temporaryLayers,
            },
          }),
          false,
          "map/addTemporaryAiMarker"
        );
      } catch (error) {}
    },

    removeTemporaryAiMarker: (workspaceId: string) => {
      const state = get();
      const newTemporaryLayers = { ...state.temporaryLayers };
      delete newTemporaryLayers[workspaceId];
      set(
        { temporaryLayers: newTemporaryLayers },
        false,
        "map/removeTemporaryAiMarker"
      );
    },

    highlightArea: async (
      workspaceId: string,
      data: {
        placeName?: string;
        geojson?: any;
        color?: string;
        opacity?: number;
      }
    ) => {
      const state = get();
      const instanceState = state.mapInstances[workspaceId];

      if (!instanceState?.isLoaded) {
        return;
      }

      get().removeHighlight(workspaceId);

      try {
        let featureData: any = null;

        if (data.placeName) {
          const countryFeature = await findCountryFeature(
            data.placeName,
            state.staticData
          );
          if (countryFeature) {
            featureData = {
              type: "FeatureCollection" as const,
              features: [countryFeature],
            };
          }
        } else if (data.geojson) {
          if (data.geojson.type === "FeatureCollection") {
            featureData = data.geojson;
          } else if (data.geojson.type === "Feature") {
            featureData = {
              type: "FeatureCollection" as const,
              features: [data.geojson],
            };
          } else {
            return;
          }
        }

        if (!featureData) {
          return;
        }

        const color = data.color || "#00297A";
        const opacity = data.opacity !== undefined ? data.opacity : 0.2;
        const fillOpacity = Math.round(opacity * 255);

        const highlightLayers: Layer[] = [
          new GeoJsonLayer({
            id: "ai-highlight-fill",
            data: featureData,
            pickable: false,
            stroked: false,
            filled: true,
            getFillColor: hexToRgb(color, fillOpacity),
          }),
          new GeoJsonLayer({
            id: "ai-highlight-border",
            data: featureData,
            pickable: false,
            stroked: true,
            filled: false,
            getLineColor: hexToRgb(color, 230),
            getLineWidth: 3,
            lineWidthMinPixels: 2,
          }),
        ];

        set(
          (state: MapLayerState) => ({
            temporaryLayers: {
              ...state.temporaryLayers,
              [workspaceId]: [
                ...(state.temporaryLayers[workspaceId] || []),
                ...highlightLayers,
              ],
            },
          }),
          false,
          "map/highlightArea"
        );
      } catch (error) {}
    },

    removeHighlight: (workspaceId: string) => {
      const state = get();
      const temporaryLayers = state.temporaryLayers[workspaceId] || [];
      const filteredLayers = temporaryLayers.filter(
        (layer: Layer) =>
          layer.id !== "ai-highlight-fill" &&
          layer.id !== "ai-highlight-border"
      );

      set(
        (state: MapLayerState) => ({
          temporaryLayers: {
            ...state.temporaryLayers,
            [workspaceId]: filteredLayers,
          },
        }),
        false,
        "map/removeHighlight"
      );
    },
  };
}

